import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { COMPANY, SERVICES } from "../lib/siteData";

const NAV = [
  { label: "Home", to: "/" },
  {
    label: "About",
    children: [
      { label: "About Company", to: "/about" },
      { label: "Vision & Mission", to: "/about#vision" },
      { label: "Leadership", to: "/about#leadership" },
    ],
  },
  { label: "Franchise", to: "/business-franchise", highlight: true },
  { label: "EV Charging", to: "/ev-charging", highlight: true },
  {
    label: "Products & Services",
    children: SERVICES.map((s) => ({ label: s.title, to: `/products/${s.slug}` })),
  },
  { label: "Projects", to: "/projects" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
    setOpenMenu(null);
  }, [pathname]);

  return (
    <header
      className="relative z-50 bg-white border-b border-slate-200 shadow-sm"
      data-testid="site-header"
    >
      {/* Top strip */}
      <div className="hidden md:block bg-slate-900 text-slate-200 text-xs">
        <div className="container-narrow flex justify-between items-center py-2">
          <div className="flex items-center gap-4">
            <a href={`mailto:${COMPANY.email}`} className="hover:text-yellow-400 transition">{COMPANY.email}</a>
            <span className="opacity-40">|</span>
            <span>{COMPANY.hours}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="opacity-70">MNRE Approved • ISO 9001:2015</span>
            <a href={`tel:${COMPANY.whatsapp}`} className="flex items-center gap-1 text-yellow-400 hover:text-yellow-300 font-semibold">
              <Phone className="w-3.5 h-3.5" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="container-narrow flex items-center justify-between h-28 lg:h-36">
        <Link to="/" className="flex items-center gap-2 group" data-testid="site-logo-link">
          <img
            src="/logo-transparent.png"
            alt="CNGTPL Solar — Clean Energy, Powering Tomorrow"
            className="h-24 sm:h-28 lg:h-[132px] w-auto object-contain transition-transform group-hover:scale-105"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="relative group">
                <button className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-slate-800 hover:text-blue-700 transition">
                  {item.label}
                  <ChevronDown className="w-4 h-4 group-hover:rotate-180 transition" />
                </button>
                <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition">
                  <div className={`bg-white rounded-xl border border-slate-200 shadow-xl py-2 ${item.children.length > 6 ? "min-w-[520px] grid grid-cols-2 gap-x-1 px-2" : "min-w-[240px]"}`}>
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 rounded transition"
                        data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `relative px-3 py-2 text-sm font-semibold transition ${
                    isActive ? "text-blue-700" : "text-slate-800 hover:text-blue-700"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
                {item.highlight && <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />}
              </NavLink>
            )
          )}
          <Link to="/calculator" className="ml-3 btn-primary text-sm px-5 py-2.5" data-testid="nav-get-quote-btn">
            Get Free Quote
          </Link>
        </nav>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-slate-900"
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200" data-testid="mobile-nav">
          <div className="container-narrow py-4 space-y-1 max-h-[80vh] overflow-y-auto">
            {NAV.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <button
                    onClick={() => setOpenMenu(openMenu === item.label ? null : item.label)}
                    className="w-full flex justify-between items-center py-3 text-slate-800 font-semibold"
                  >
                    {item.label}
                    <ChevronDown className={`w-4 h-4 transition ${openMenu === item.label ? "rotate-180" : ""}`} />
                  </button>
                  {openMenu === item.label && (
                    <div className="pl-4 space-y-1 pb-2">
                      {item.children.map((c) => (
                        <Link key={c.to} to={c.to} className="block py-2 text-sm text-slate-600 hover:text-blue-700">
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={item.to} to={item.to} className="block py-3 text-slate-800 font-semibold hover:text-blue-700">
                  {item.label}
                </Link>
              )
            )}
            <Link to="/calculator" className="btn-primary w-full mt-3" data-testid="mobile-nav-cta">
              Get Free Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
